import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { Formik, FormikErrors, FormikProps } from "formik";
import { Button } from "../shared/Button/Button";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  CreateAccountButtonContainer,
  CreateAccountInput,
  CreateAccountLoginMessageContainer,
  CreateAccountCenterContainer,
  Spinner,
  CreateAccountFormStyled,
  CreateAccountPassword,
  CreateAccountPasswordInfoContainer,
  CreateAccountFormStyledContainer,
  ButtonContainer,
  CreateAccountCenterFormContainer,
  CreateAccountPrivacyPolicy,
} from "./CreateAccountForm.styles";
import {
  auth0ResponseMessages,
  CheckCreateAccountToken,
  CreateAccountRequest,
} from "./Services/CreateAccountService";
import { CreateAccountValuesType } from "./types";
import { LANDING_PAGE_URL, LOGIN_PAGE_URL } from "../../utils/urlConstants";
import { createAccountInitialValues } from "./Services/CreateAccountServiceHelpers";
import { CreateAccountPasswordInfo } from "./CreateAccountPasswordInfo";
import loading from "../../assets/icons/loading.svg";
import {
  providerState,
  defaultRequisitions,
  defaultReqsRequestComplete,
} from "../../recoil/atoms";
import { UserAuthProviderResponse } from "../../recoil/types";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import { RefreshRequisitionRows } from "../Grid/Services/GridService";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { setCookieAccessToken } from "../../utils/requestUtils";

export const CreateAccountFormContent = (): React.ReactElement => {
  const history = useHistory();
  const { jwtToken } = useParams<{ jwtToken: string }>();
  const { auth, setLoggedOut } = useAuthenticationHook();
  const [email, setEmail] = useState<string>();

  useEffect(() => {
    if (!jwtToken) {
      history.replace(LOGIN_PAGE_URL);
      return;
    }

    if (auth) {
      history.replace(LANDING_PAGE_URL);
      return;
    }

    const checkToken = async () => {
      const response = await CheckCreateAccountToken(jwtToken, setLoggedOut);
      if (response[0] === 200) {
        setEmail(response[1]?.email);
      } else if (response[0] === 201) {
        history.push(LOGIN_PAGE_URL);
      } else if (response[0] === 400) {
        setErrorMessage("Email Expired - Please contact us to resend.");
      } else if (response[0] === 409) {
        setErrorMessage("Account already exists. Please login.");
      } else if (response[0] === 401) {
        history.push(LOGIN_PAGE_URL);
      } else {
        setErrorMessage("Please refresh page to try again.");
      }
    };

    checkToken();
  }, [auth, history, jwtToken, setLoggedOut]);

  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setProviderInfo] = useRecoilState(providerState);
  const [, setRequisitions] = useRecoilState(defaultRequisitions);
  const [, setDefaultReqsComplete] = useRecoilState(defaultReqsRequestComplete);
  const [isPw8Chars, setIsPw8Chars] = useState(false);
  const [isPwUpperChar, setIsPwUpperChar] = useState(false);
  const [isPwLowerChar, setIsPwLowerChar] = useState(false);
  const [isPwDigit, setIsPwDigit] = useState(false);
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const [doPasswordsMatch, setDoPasswordsMatch] = useState(false);
  const { deviceType } = useDeviceHook();

  const checkPasswordRequirements = (input: string): boolean => {
    const pw8Chars = input.length >= 8;
    const pwUpperChar = /[A-Z]/.test(input);
    const pwLowerChar = /[a-z]/.test(input);
    const pwDigit = /[0-9]/.test(input);
    setIsPw8Chars(pw8Chars);
    setIsPwUpperChar(pwUpperChar);
    setIsPwLowerChar(pwLowerChar);
    setIsPwDigit(pwDigit);
    return (
      pw8Chars && pwUpperChar && pwLowerChar && pwDigit && doPasswordsMatch
    );
  };

  const onSubmit = async ({
    password,
  }: CreateAccountValuesType): Promise<void> => {
    setIsSubmitting(true);
    try {
      const response = await CreateAccountRequest(
        {
          password,
          token: jwtToken,
        },
        setLoggedOut,
      );
      setIsSubmitting(false);
      const statusCode: number = response[0];
      const newUserInfo: UserAuthProviderResponse | undefined = response[1];
      if (newUserInfo?.token) {
        setCookieAccessToken(newUserInfo?.token);
        setProviderInfo(newUserInfo);
        await RefreshRequisitionRows(
          newUserInfo.token,
          setRequisitions,
          setLoggedOut,
          undefined,
          setDefaultReqsComplete,
        );
        history.push(LANDING_PAGE_URL);
      } else if (statusCode === 409) {
        setErrorMessage("Account already exists. Please login.");
      } else {
        setErrorMessage(`${auth0ResponseMessages[statusCode]}`);
      }
    } catch (e) {
      setIsSubmitting(false);
      setErrorMessage(`Check network connection`);
    }
  };

  return (
    <CreateAccountCenterContainer deviceType={deviceType}>
      <ErrorPopup message={errorMessage} hideMessage={setErrorMessage} onTop />
      <CreateAccountCenterFormContainer>
        {GetTypographyContent({
          content: "Welcome to Unity",
          size: "xlarge",
          textColor: "neutral700",
          padding: deviceType === "mobile" ? "40px 0 0" : "75px 0 0 0",
        })}
        {GetTypographyContent({
          content: "Please finish account",
          size: "h3",
          isBold: true,
          textColor: "neutral700",
          padding: "5px 0 0 0",
        })}
        <CreateAccountLoginMessageContainer>
          {GetTypographyContent({
            content:
              "Welcome to the provider portal, for security reasons please help us create a password ",
            size: "medium",
            textColor: "neutral700",
          })}
        </CreateAccountLoginMessageContainer>
        <CreateAccountLoginMessageContainer>
          {GetTypographyContent({
            content: "Already have an account?",
            size: "medium",
            textColor: "neutral700",
          })}
          {GetTypographyContent({
            content: "Log in",
            size: "medium",
            textColor: "greenDark",
            link: LOGIN_PAGE_URL,
          })}
        </CreateAccountLoginMessageContainer>
        <Formik
          initialValues={createAccountInitialValues}
          onSubmit={(values) => {
            onSubmit(values);
          }}
          validateOnChange={false}
          validate={(values) => {
            const errors: FormikErrors<CreateAccountValuesType> = {};
            if (!values.password) {
              errors.password = "Required";
            } else if (
              !checkPasswordRequirements(values.password) &&
              doPasswordsMatch
            ) {
              errors.password = "Password not valid";
            } else if (!checkPasswordRequirements(values.password)) {
              errors.password = "Passwords don't match";
            }

            return errors;
          }}
        >
          {({
            values,
            errors,
            handleSubmit,
            setFieldValue,
            setFieldError,
          }: FormikProps<CreateAccountValuesType>) => (
            <CreateAccountFormStyledContainer deviceType={deviceType}>
              <CreateAccountFormStyled deviceType={deviceType}>
                <CreateAccountInput
                  type="email"
                  name="email"
                  value={email || ""}
                  placeholder="Email"
                  label="Email"
                  size="small"
                  disabled
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  error={errors.email}
                />

                <CreateAccountPassword
                  type="password"
                  name="password"
                  value={values.password}
                  placeholder="Password"
                  autoComplete="on"
                  onChange={(event) => {
                    setFieldError("password", "");
                    checkPasswordRequirements(event.target.value);
                    setFieldValue("password", event.target.value);
                    setDoPasswordsMatch(
                      event.target.value === confirmedPassword,
                    );
                  }}
                  label="Password"
                  size="small"
                  error={Boolean(errors.password)}
                  helperText={errors.password}
                />
                <CreateAccountPassword
                  type="password"
                  name="password"
                  autoComplete="on"
                  value={confirmedPassword}
                  placeholder="Confirm Password"
                  onChange={(event) => {
                    setConfirmedPassword(event.target.value);
                    setDoPasswordsMatch(event.target.value === values.password);
                  }}
                  label="Confirm Password"
                  size="small"
                />

                <CreateAccountPasswordInfoContainer>
                  <CreateAccountPasswordInfo
                    doPasswordsMatch={doPasswordsMatch}
                    isPw8Chars={isPw8Chars}
                    isPwDigit={isPwDigit}
                    isPwUpperChar={isPwUpperChar}
                    isPwLowerChar={isPwLowerChar}
                  />
                </CreateAccountPasswordInfoContainer>
                <CreateAccountButtonContainer>
                  {jwtToken && isSubmitting && (
                    <Spinner>
                      <img src={loading} alt="Loading" />
                    </Spinner>
                  )}
                  <ButtonContainer>
                    <Button
                      type="submit"
                      label="Create Account"
                      size="medium"
                      disabled={!email}
                      onClick={handleSubmit}
                    />
                  </ButtonContainer>
                </CreateAccountButtonContainer>
                <CreateAccountPrivacyPolicy>
                  {/* <p>
                      {`By clicking 'Create Account' you are authorizing
                    BillionToOne to contact you for test status,
                    billing/collection, quality assurance, research or
                    marketing purposes. If you would not like to receive
                    marketing communications, please email `}
                      <a href="mailto:media@billiontoone.com" target="_">
                        media@billiontoone.com.
                      </a>
                    </p> */}
                  <p>
                    {`By clicking 'Create Account' you agree to our `}
                    <a
                      style={{ textDecoration: "underline" }}
                      href="https://www.billiontoone.com/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                    {`, `}
                    <a
                      style={{ textDecoration: "underline" }}
                      href="https://www.billiontoone.com/privacy-practices"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Notice of Privacy Practices
                    </a>
                    {`, and `}
                    <a
                      style={{ textDecoration: "underline" }}
                      href="../../provider/terms_of_use"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms of Use.
                    </a>
                  </p>
                </CreateAccountPrivacyPolicy>
              </CreateAccountFormStyled>
            </CreateAccountFormStyledContainer>
          )}
        </Formik>
      </CreateAccountCenterFormContainer>
    </CreateAccountCenterContainer>
  );
};
